/**
 * Designing a fab: the conversation, the floor, and what the floor can make.
 *
 * THREE COLUMNS AND ONE STATE. The assistant on the left, the row of
 * stations in the middle, the product-route reading on the right - and every
 * one of them is a projection of the same `plan` object. Typing "add a CMP
 * polisher" and clicking the + beside CMP-11 go through the identical edit
 * in `assistant/author/edits.ts`, so the two can never disagree about what
 * the floor is, and the coverage panel is recomputed from the plan rather
 * than patched.
 *
 * NOTHING IS SAVED UNTIL SOMEBODY SAYS SO. The plan here is local state.
 * That matters because the assistant can act on a whole floor at once -
 * "start again from an OSAT" throws away a front end - and an author has to
 * be able to see what a sentence did and step back from it. Hence the undo
 * stack, which is the plan before each edit and nothing cleverer.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input, Segmented, Tag, Tooltip } from 'antd'
import { t, tOr } from '../../i18n/i18n'
import {
  deletePlan,
  listPlans,
  newPlanId,
  savePlan,
  uniqueName,
  type FloorPlan,
  type PlanHall,
} from '../../fab/floorPlan'
import { currentHall, currentPlan, hallIdFor, refreshCustomHall, setHall } from '../../fab/hall'
import { planFootprint, REQUIRED_TOOL_IDS } from '../../fab/layout'
import { segmentComplete, SEGMENT_IDS, type FloorCoverage } from '../../sim/flow/coverage'
import { ARCHETYPES } from '../../assistant/author/archetypes'
import {
  AUTHOR_ACTIONS,
  exampleOf,
  isQuestion,
  summaryOf,
} from '../../assistant/author/actions'
import {
  askAuthor,
  coverageForPlan,
  describeCoverage,
} from '../../assistant/author/assistant'
import {
  idsInZone,
  labelOf,
  nameOf,
  STATIONS,
  tagOf,
  ZONES,
} from '../../assistant/author/catalogue'
import {
  addTools,
  didNothing,
  moveTool,
  removeTools,
  renamePlan,
  setPlanHall,
  sortToFlow,
  useArchetype,
  type Change,
} from '../../assistant/author/edits'
import { showLoadingThen } from '../shell/loading'
import { confirmDialog } from './confirm'

interface Turn {
  who: 'author' | 'assistant'
  lines: string[]
  /** True when this device routed the request rather than a model. */
  local?: boolean
}

const REQUIRED = new Set(REQUIRED_TOOL_IDS)

/** A floor with nothing on it but the way in. */
const blankPlan = (): FloorPlan => ({
  id: newPlanId(),
  name: uniqueName(tOr('author.NewFloor', 'New floor')),
  hall: 'bayChase',
  tools: [...REQUIRED_TOOL_IDS],
  savedAt: Date.now(),
})

/**
 * The opening move.
 *
 * A blank floor is a blank page, so the assistant does not wait to be asked:
 * it names the six fabs that exist and says they are a starting point. The
 * chips under it are those six, and clicking one is the same edit typing it
 * would have made.
 */
const greeting = (plan: FloorPlan): Turn => ({
  who: 'assistant',
  lines: [
    tOr('author.Subtitle', ''),
    plan.tools.length > 1
      ? t('author.reply.Floor', { n: plan.tools.length, tools: plan.tools.map(tagOf).join(', ') })
      : t('author.reply.EmptyFloor'),
  ],
})

/**
 * The aisle, drawn.
 *
 * Bays down the middle line with a face either side, exactly the way
 * `layout.ts` lays a custom floor out - two stations to a bay, the first of
 * each pair on the near side. It is the one place an author can see that the
 * order they chose IS the order a wafer walks, which is the point of letting
 * them order it at all.
 */
function AislePlan({ plan }: { plan: FloorPlan }) {
  const axis: string[] = plan.tools.filter((id) => id === 'gowning' || id === 'gas')
  const row = plan.tools.filter((id) => !axis.includes(id))
  const bays: string[][] = []
  row.forEach((id, i) => {
    if (i % 2 === 0) bays.push([id])
    else bays[bays.length - 1].push(id)
  })
  if (!bays.length) return null
  const pitch = 64
  const width = bays.length * pitch + 80
  return (
    <div className="author-plan">
      <svg width={width} height={92} role="img" aria-label={tOr('author.OnTheFloor', '')}>
        <line x1={8} y1={46} x2={width - 8} y2={46} className="author-plan-aisle" />
        {plan.tools.includes('gowning') && (
          <text x={10} y={36} className="author-plan-axis">
            {tagOf('gowning')}
          </text>
        )}
        {bays.map((bay, i) => {
          const x = 44 + i * pitch
          return (
            <g key={i}>
              {bay.map((id, side) => (
                <g key={id}>
                  <rect
                    x={x - 26}
                    y={side === 0 ? 8 : 58}
                    width={52}
                    height={26}
                    rx={4}
                    className="author-plan-face"
                  />
                  <text x={x} y={side === 0 ? 25 : 75} className="author-plan-tag">
                    {tagOf(id)}
                  </text>
                </g>
              ))}
              <text x={x} y={50} className="author-plan-bay">
                {String(i + 1).padStart(2, '0')}
              </text>
            </g>
          )
        })}
        {plan.tools.includes('gas') && (
          <text x={width - 10} y={36} textAnchor="end" className="author-plan-axis">
            {tagOf('gas')}
          </text>
        )}
      </svg>
    </div>
  )
}

/** The product route's reading on this floor, segment by segment. */
function CoveragePanel({ coverage }: { coverage: FloorCoverage }) {
  const pct = coverage.steps ? Math.round((coverage.covered / coverage.steps) * 100) : 0
  return (
    <div className="author-coverage">
      <div className="author-coverage-head">{tOr('author.CoverageTitle', 'Against the product route')}</div>
      <div className="author-coverage-bar">
        <div className="author-coverage-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="author-coverage-num">
        {t('author.CoverageBar', { covered: coverage.covered, steps: coverage.steps })} · {pct}%
      </div>
      <div className="author-coverage-num">
        {t('author.MaskCount', { masksCovered: coverage.masksCovered, masks: coverage.masks })}
      </div>
      <ul className="author-segments">
        {SEGMENT_IDS.map((id) => {
          const s = coverage.segments.find((x) => x.id === id)
          if (!s) return null
          const whole = segmentComplete(s)
          return (
            <li key={id} className={whole ? 'on' : ''}>
              <span className="author-segment-name">{tOr(`author.segment.${id}`, id)}</span>
              <span className="author-segment-state">
                {whole
                  ? tOr('author.SegmentRuns', 'runs end to end')
                  : t('author.SegmentPart', { covered: s.covered, steps: s.steps })}
              </span>
            </li>
          )
        })}
      </ul>
      <div className="author-coverage-prose">
        {describeCoverage(coverage, { segments: false }).map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>
    </div>
  )
}

export function FabAuthorView({ onClose }: { onClose: () => void }) {
  // The fab you are standing in, if it is one you built - so "Rebuild and
  // walk in" edits THIS floor rather than a blank one you have to find in
  // the saved list first. Anywhere else, a blank floor.
  const [plan, setPlan] = useState<FloorPlan>(() => currentPlan() ?? blankPlan())
  const [history, setHistory] = useState<FloorPlan[]>([])
  const [turns, setTurns] = useState<Turn[]>(() => [greeting(plan)])
  // The author's name as typed, applied as one undoable edit on blur.
  const [nameDraft, setNameDraft] = useState(plan.name)
  useEffect(() => setNameDraft(plan.name), [plan.name])
  // Always the plan as it stands, for a reply that arrives after a click.
  const planRef = useRef(plan)
  planRef.current = plan
  const [question, setQuestion] = useState('')
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState<FloorPlan[]>(() => listPlans())
  const logRef = useRef<HTMLDivElement>(null)

  const coverage = useMemo(() => coverageForPlan(plan), [plan])
  const footprint = useMemo(() => planFootprint(plan.tools, plan.hall), [plan])
  const standingHere = currentHall() === hallIdFor(plan.id)

  // The newest line, in view. The log is the only thing in this dialog that
  // grows, and a reply the author has to scroll to find is a reply they
  // will miss the consequence in.
  useEffect(() => {
    const el = logRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [turns])

  /** Apply an edit from a button, with the same undo the assistant gets. */
  const apply = (change: Change): void => {
    if (didNothing(change)) return
    setHistory((h) => [...h, plan])
    setPlan(change.plan)
  }

  const ask = async (text: string): Promise<void> => {
    const trimmed = text.trim()
    if (!trimmed || busy) return
    setQuestion('')
    setTurns((ts) => [...ts, { who: 'author', lines: [trimmed] }])
    setBusy(true)
    try {
      // Against the plan as it stands when the reply comes back, not as it
      // stood when the question left: every edit control is disabled while
      // a request is in flight, but the ref is what makes that a guarantee
      // rather than a hope.
      const base = planRef.current
      const reply = await askAuthor(base, trimmed)
      if (reply.plan !== base) {
        setHistory((h) => [...h, base])
        setPlan(reply.plan)
      }
      const lines = [...reply.lines]
      if (reply.offer) lines.push(`"${reply.offer.example}"`)
      setTurns((ts) => [...ts, { who: 'assistant', lines, local: reply.local }])
    } finally {
      setBusy(false)
    }
  }

  const undo = (): void => {
    setHistory((h) => {
      if (!h.length) return h
      setPlan(h[h.length - 1])
      return h.slice(0, -1)
    })
  }

  const openSaved = (p: FloorPlan): void => {
    setHistory((h) => [...h, plan])
    setPlan(p)
    setTurns((ts) => [...ts, { who: 'assistant', lines: [t('author.reply.Floor', { n: p.tools.length, tools: p.tools.map(tagOf).join(', ') })] }])
  }

  const save = (): void => {
    savePlan(plan)
    setSaved(listPlans())
  }

  /**
   * Save it, build it, and walk in.
   *
   * Two ways in, because the hall may already be this one: a floor its
   * author is standing in is RE-laid where it stands (hall.ts
   * `refreshCustomHall`), and any other is a fab switch like Fab A to Fab B,
   * loading screen and all.
   */
  const build = (): void => {
    savePlan(plan)
    setSaved(listPlans())
    onClose()
    const id = hallIdFor(plan.id)
    showLoadingThen(id, () => {
      if (currentHall() === id) refreshCustomHall(plan.id)
      else setHall(id)
    })
  }

  const remove = async (p: FloorPlan): Promise<void> => {
    if (currentHall() === hallIdFor(p.id)) return
    const yes = await confirmDialog({
      title: tOr('author.Delete', 'Delete'),
      body: t('author.DeleteConfirm', { name: p.name }),
      okText: tOr('author.Delete', 'Delete'),
      cancelText: tOr('author.Cancel', 'Keep it'),
    })
    if (!yes) return
    deletePlan(p.id)
    setSaved(listPlans())
  }

  const onFloor = new Set(plan.tools)
  const offFloor = STATIONS.filter((s) => !onFloor.has(s.id))

  return (
    <div className="author">
      <div className="author-col author-chat">
        <div className="author-log" ref={logRef}>
          {turns.map((turn, i) => (
            <div key={i} className={'author-turn ' + turn.who}>
              {turn.lines.map((line, j) => (
                <p key={j}>{line}</p>
              ))}
              {turn.who === 'assistant' && turn.local && (
                <span className="author-local">{tOr('author.Local', 'matched on this device')}</span>
              )}
            </div>
          ))}
        </div>

        <div className="author-chips">
          {ARCHETYPES.map((a) => (
            <Tooltip key={a.id} title={tOr(`author.archetype.${a.id}.Purpose`, '')}>
              <button className="author-chip" disabled={busy} onClick={() => apply(useArchetype(plan, a.id))}>
                {tOr(`author.archetype.${a.id}.Name`, a.id)}
              </button>
            </Tooltip>
          ))}
        </div>

        <div className="author-ask">
          <Input.TextArea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={tOr('author.Ask', 'Ask for a change…')}
            autoSize={{ minRows: 2, maxRows: 4 }}
            disabled={busy}
            onPressEnter={(e) => {
              if (e.shiftKey) return
              e.preventDefault()
              void ask(question)
            }}
          />
          <Button type="primary" loading={busy} onClick={() => void ask(question)}>
            {busy ? tOr('author.Working', 'Working…') : tOr('author.Send', 'Send')}
          </Button>
        </div>

        <div className="author-suggestions">
          <span className="author-suggest-head">{tOr('author.Suggestions', 'Try asking')}</span>
          {AUTHOR_ACTIONS.filter((id) => isQuestion(id) || id === 'add' || id === 'hall').map((id) => (
            <Tooltip key={id} title={summaryOf(id)}>
              <button className="author-chip small" disabled={busy} onClick={() => void ask(exampleOf(id))}>
                {exampleOf(id)}
              </button>
            </Tooltip>
          ))}
        </div>
      </div>

      <div className="author-col author-floor">
        <div className="author-name">
          <Input
            value={nameDraft}
            maxLength={60}
            disabled={busy}
            placeholder={tOr('author.NamePlaceholder', 'Name this fab')}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={() => apply(renamePlan(plan, nameDraft))}
            onPressEnter={() => apply(renamePlan(plan, nameDraft))}
          />
          <Segmented
            value={plan.hall}
            disabled={busy}
            onChange={(v) => apply(setPlanHall(plan, v as PlanHall))}
            options={(['bayChase', 'ballroom'] as PlanHall[]).map((h) => ({
              value: h,
              label: tOr(`author.hall.${h}`, h),
            }))}
          />
        </div>
        <div className="author-size">
          {t('author.HallSize', { length: Math.round(footprint.lengthM), bays: footprint.bays })}
        </div>

        <AislePlan plan={plan} />

        <div className="author-listhead">
          <span>{tOr('author.OnTheFloor', 'On the floor')}</span>
          <span className="author-listactions">
            <Button size="small" disabled={busy} title={summaryOf('sort')} onClick={() => apply(sortToFlow(plan))}>
              {tOr('author.SortShort', 'Flow order')}
            </Button>
            <Button size="small" disabled={busy || !history.length} onClick={undo}>
              {tOr('author.Undo', 'Undo')}
            </Button>
          </span>
        </div>

        <ul className="author-tools">
          {plan.tools.map((id, i) => (
            <li key={id}>
              <span className="author-tool-tag">{tagOf(id)}</span>
              <span className="author-tool-name">{nameOf(id)}</span>
              <span className="author-tool-buttons">
                <Tooltip title={tOr('author.MoveEarlier', '')}>
                  <button disabled={busy || i === 0} onClick={() => apply(moveTool(plan, id, -1))}>
                    ↑
                  </button>
                </Tooltip>
                <Tooltip title={tOr('author.MoveLater', '')}>
                  <button disabled={busy || i === plan.tools.length - 1} onClick={() => apply(moveTool(plan, id, 1))}>
                    ↓
                  </button>
                </Tooltip>
                <Tooltip title={REQUIRED.has(id) ? tOr('author.RequiredTool', '') : tOr('author.RemoveTool', '')}>
                  <button disabled={busy || REQUIRED.has(id)} onClick={() => apply(removeTools(plan, [id]))}>
                    ×
                  </button>
                </Tooltip>
              </span>
            </li>
          ))}
        </ul>

        <div className="author-listhead">{tOr('author.Palette', 'Not on this floor')}</div>
        <div className="author-palette">
          {ZONES.map((zone) => {
            const ids = idsInZone(zone).filter((id) => !onFloor.has(id))
            if (!ids.length) return null
            return (
              <div key={zone} className="author-palette-zone">
                <span className="author-palette-head">{tOr(`author.zone.${zone}`, zone)}</span>
                {ids.map((id) => (
                  <Tooltip key={id} title={`${nameOf(id)} — ${tOr('author.AddTool', 'Put on the floor')}`}>
                    <button className="author-chip small" disabled={busy} onClick={() => apply(addTools(plan, [id]))}>
                      + {labelOf(id)}
                    </button>
                  </Tooltip>
                ))}
              </div>
            )
          })}
          {!offFloor.length && <span className="author-palette-head">{tOr('author.Empty', '')}</span>}
        </div>
      </div>

      <div className="author-col author-side">
        <CoveragePanel coverage={coverage} />

        <div className="author-actions">
          <Button type="primary" disabled={busy} onClick={build}>
            {standingHere ? tOr('author.Rebuild', 'Rebuild and walk in') : tOr('author.Build', 'Build it and walk in')}
          </Button>
          <Button disabled={busy} onClick={save}>{tOr('author.Save', 'Save')}</Button>
        </div>

        <div className="author-listhead">{tOr('author.SavedFloors', 'Fabs you built')}</div>
        {!saved.length && <p className="author-empty">{tOr('author.NoSaved', '')}</p>}
        <ul className="author-saved">
          {saved.map((p) => {
            const here = currentHall() === hallIdFor(p.id)
            return (
              <li key={p.id}>
                <button className="author-saved-open" onClick={() => openSaved(p)}>
                  {p.name}
                </button>
                <span className="author-saved-note">
                  {t('menu.CustomFabNote', { n: p.tools.length, hall: tOr(`author.hall.${p.hall}`, p.hall) })}
                </span>
                {here ? (
                  <Tooltip title={tOr('author.DeleteActive', '')}>
                    <Tag color="green">{tOr('author.Standing', 'You are standing in this one')}</Tag>
                  </Tooltip>
                ) : (
                  <Tooltip title={tOr('author.Delete', 'Delete')}>
                    <button className="author-saved-del" onClick={() => void remove(p)}>
                      ×
                    </button>
                  </Tooltip>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
